/**
 * ExpressionField — Input field with expression toggle
 *
 * Wraps a text input with a { } toggle button. When enabled:
 * - Field becomes an expression field with {{ }} syntax
 * - Shows autocomplete dropdown for upstream node references
 * - Preview of resolved value (if test data available)
 *
 * Syntax: {{ $input.json.field }}, {{ $node['name'].json.field }}, {{ $vars.x }}
 */

import { useState, useRef } from 'react';
import { Braces, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

// ── Available variables for autocomplete ────────────────────────────────────

const EXPRESSION_SUGGESTIONS = [
  { path: '$input.json', description: 'Current step input data', category: 'Input' },
  { path: '$input.json.items', description: 'Input items array', category: 'Input' },
  { path: '$input.json.status', description: 'Input status field', category: 'Input' },
  { path: '$trigger.json', description: 'Trigger event data', category: 'Trigger' },
  { path: '$trigger.json.eventType', description: 'Event type from trigger', category: 'Trigger' },
  { path: '$trigger.json.payload', description: 'Trigger payload', category: 'Trigger' },
  { path: '$vars.threshold', description: 'Workflow variable', category: 'Variables' },
  { path: '$vars.department', description: 'Workflow variable', category: 'Variables' },
  { path: '$vars.maxRetries', description: 'Workflow variable', category: 'Variables' },
  { path: '$execution.id', description: 'Current execution ID', category: 'Execution' },
  { path: '$execution.mode', description: 'Execution mode (test/production)', category: 'Execution' },
  { path: '$execution.startedAt', description: 'Execution start time', category: 'Execution' },
  { path: "$node['Asset Lookup'].json", description: 'Output from Asset Lookup node', category: 'Nodes' },
  { path: "$node['Get Telemetry'].json", description: 'Output from Get Telemetry node', category: 'Nodes' },
  { path: "$node['Analyze'].json.diagnosis", description: 'Diagnosis from analysis', category: 'Nodes' },
  { path: "$node['Analyze'].json.severity", description: 'Severity from analysis', category: 'Nodes' },
];

interface ExpressionFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  className?: string;
}

export function ExpressionField({ value, onChange, placeholder, multiline, className }: ExpressionFieldProps) {
  const [isExpression, setIsExpression] = useState(value.includes('{{'));
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const handleInsert = (path: string) => {
    const expr = `{{ ${path} }}`;
    const cursorPos = inputRef.current?.selectionStart ?? value.length;
    const newValue = value.slice(0, cursorPos) + expr + value.slice(cursorPos);
    onChange(newValue);
    setShowSuggestions(false);
    setIsExpression(true);
  };

  const hasExpressions = value.includes('{{');

  return (
    <div className={cn('relative', className)}>
      <div className="flex items-center gap-1 mb-1">
        <Button
          variant={isExpression ? 'secondary' : 'ghost'}
          size="sm"
          className={cn('h-5 px-1.5 text-[9px] gap-0.5', isExpression && 'bg-violet-500/10 text-violet-600 dark:text-violet-400')}
          onClick={() => setIsExpression(!isExpression)}
          title={isExpression ? 'Switch to fixed value' : 'Switch to expression'}
        >
          <Braces className="h-2.5 w-2.5" />
          {isExpression ? 'Expression' : 'Fixed'}
        </Button>

        {isExpression && (
          <Popover open={showSuggestions} onOpenChange={setShowSuggestions}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[9px] gap-0.5" title="Insert variable reference">
                <ChevronDown className="h-2.5 w-2.5" /> Insert
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <ScrollArea className="max-h-[250px]">
                <div className="p-2 space-y-2">
                  {['Input', 'Trigger', 'Nodes', 'Variables', 'Execution'].map(cat => {
                    const items = EXPRESSION_SUGGESTIONS.filter(s => s.category === cat);
                    if (items.length === 0) return null;
                    return (
                      <div key={cat}>
                        <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider px-1 mb-1">{cat}</p>
                        {items.map(s => (
                          <button
                            key={s.path}
                            className="w-full text-left px-2 py-1.5 rounded hover:bg-muted/50 transition-colors"
                            onClick={() => handleInsert(s.path)}
                          >
                            <span className="text-[10px] font-mono text-primary">{s.path}</span>
                            <p className="text-[9px] text-muted-foreground">{s.description}</p>
                          </button>
                        ))}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </PopoverContent>
          </Popover>
        )}

        {hasExpressions && (
          <Badge variant="outline" className="text-[7px] h-4 px-1 ml-auto text-violet-500">dynamic</Badge>
        )}
      </div>

      {multiline ? (
        <Textarea
          ref={inputRef as any}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={isExpression ? 'Type {{ to insert a variable reference...' : placeholder}
          className={cn('text-xs', isExpression && 'font-mono bg-violet-500/[0.03] border-violet-500/20')}
          rows={3}
        />
      ) : (
        <Input
          ref={inputRef as any}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={isExpression ? '{{ $input.json.field }}' : placeholder}
          className={cn('h-8 text-xs', isExpression && 'font-mono bg-violet-500/[0.03] border-violet-500/20')}
        />
      )}

      {/* Preview resolved value (mock) */}
      {isExpression && hasExpressions && (
        <div className="mt-1 px-2 py-1 bg-muted/30 rounded text-[9px] text-muted-foreground font-mono">
          → <span className="text-foreground">{value.includes('severity') ? '"high"' : value.includes('assetId') ? '"P-401"' : value.includes('items') ? '[...3 items]' : '(preview unavailable)'}</span>
        </div>
      )}
    </div>
  );
}
